import React, { useState } from "react";
import { api } from "../../lib/api";
import { Download, Upload, DatabaseBackup } from "lucide-react";
import { toast } from "sonner";

export default function AdminBackup() {
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const onDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/db/backup", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `gamestrade_backup_${stamp}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch {
      toast.error("Backup failed");
    } finally {
      setDownloading(false);
    }
  };

  const onRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const typed = window.prompt(
      "This will PERMANENTLY REPLACE all live data with the contents of this backup file.\n\nType RESTORE to confirm."
    );
    if (typed !== "RESTORE") {
      e.target.value = "";
      return;
    }

    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/db/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Restored ${r.data.restored_collections.length} collections`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(false);
      e.target.value = "";
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-black uppercase tracking-tighter mb-1">Database Backup</h1>
      <p className="text-sm text-slate-400 mb-8">Download a full snapshot of the database, or restore from a previous backup.</p>

      <div className="bg-navy-800 rounded-2xl p-6 border border-gold/20 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <DatabaseBackup className="w-5 h-5 text-gold" />
          <h2 className="text-lg font-black uppercase tracking-tight">Download Backup</h2>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          Downloads every collection in the database as a single .zip archive to your computer.
        </p>
        <button
          onClick={onDownload}
          disabled={downloading}
          className="border border-gold/30 text-gold font-extrabold uppercase tracking-wider text-xs px-4 py-2.5 rounded-full hover:bg-gold/10 disabled:opacity-50 flex items-center gap-2"
        >
          <Download className="w-4 h-4" /> {downloading ? "Preparing..." : "Download Backup"}
        </button>
      </div>

      <div className="bg-navy-800 rounded-2xl p-6 border border-red-500/30">
        <div className="flex items-center gap-3 mb-3">
          <Upload className="w-5 h-5 text-red-400" />
          <h2 className="text-lg font-black uppercase tracking-tight">Restore Backup</h2>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          Uploads a backup .zip and replaces all live data with its contents. A safety snapshot of the
          current data is kept for the duration of the request and used to roll back automatically if
          the restore fails partway through. This is destructive and cannot be undone once complete —
          make sure you have a recent backup downloaded first.
        </p>
        <label className={`border border-red-500/40 text-red-400 font-extrabold uppercase tracking-wider text-xs px-4 py-2.5 rounded-full hover:bg-red-500/10 flex items-center gap-2 w-fit ${restoring ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}>
          <Upload className="w-4 h-4" /> {restoring ? "Restoring..." : "Restore from Backup"}
          <input type="file" accept=".zip" onChange={onRestore} className="hidden" />
        </label>
      </div>
    </div>
  );
}
